// src/components/wellfinder/WellMapLegend.jsx
//
// The detection legend on the WellFinder PRODUCT map.
//
// ReviewMap has had MapLegend since it shipped; this map had none, and until
// the wellfinder_detections tile layer landed it did not need one — every well
// painted the same colour. It carries six encodings now, and not one of them is
// inferable: grey and blue differ by verdict, which is nothing an analyst could
// work out from the frame.
//
// Every swatch is DERIVED from wellMapEncoding.js, the same object WellLayer's
// paint expressions are built from. A hand-written legend is one that quietly
// stops being true, and a legend that lies about what blue means is worse than
// no legend at all.
//
// Not dismissible and not behind a setting, for the same reason MapLegend is
// not: it is six lines and the encoding is not self-evident.

import { WELL_LEGEND_ITEMS, WELL_ENCODING } from "./wellMapEncoding";

// The map draws these at radius 4–6 depending on zoom. Fixed here at a size
// that reads in a HUD, with the ring kept proportional — pending's white ring
// is the widest and has to survive the shrink, because "has a white ring" is
// the whole distinction between not-reviewed and decided.
const SWATCH_PX = 9;

function Swatch({ item }) {
  const hollow = item.key === WELL_ENCODING.rejected.key;
  return (
    <span
      aria-hidden="true"
      data-testid={`well-legend-swatch-${item.key}`}
      data-color={hollow ? item.stroke : item.fill}
      className="inline-block shrink-0 rounded-full align-middle"
      style={{
        width: SWATCH_PX,
        height: SWATCH_PX,
        backgroundColor: item.fill,
        // box-shadow rather than border so the swatch keeps its diameter and
        // the six dots stay on one optical line.
        boxShadow: `0 0 0 ${Math.min(item.strokeWidth, 2)}px ${item.stroke}`,
      }}
    />
  );
}

export default function WellMapLegend() {
  return (
    <div
      data-testid="well-map-legend"
      // NOT absolutely positioned. It is a member of MapView's bottom stack,
      // above the lat/lng readout and the time strip — self-start so it is only
      // as wide as its longest row rather than stretching the full map width.
      //
      // It WAS absolute bottom-2 left-2 and covered the readout. A hardcoded
      // offset would have had to know the readout's height AND whether the time
      // strip is mounted, which changes with the coverage flyout; stacking makes
      // that the browser's problem, which is the same reasoning the readout and
      // the strip already sit in one flex column for.
      className="pointer-events-none self-start rounded border border-bg-border
                 bg-bg/85 px-2 py-1.5 backdrop-blur-sm"
    >
      <div className="mb-1 text-[9px] font-mono uppercase tracking-wider text-fg-secondary/70">
        Detections
      </div>
      <ul className="space-y-1">
        {WELL_LEGEND_ITEMS.map((item) => (
          <li
            key={item.key}
            data-testid={`well-legend-item-${item.key}`}
            className="flex items-center gap-2 text-[10px] leading-none text-fg-secondary"
          >
            <span className="flex w-4 justify-center">
              <Swatch item={item} />
            </span>
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
